import { useMemo } from 'react';
import { useUserDetails } from '@/api/query/useUserDetails';
import { getStoredUser, setStoredUser } from '@/utils/auth';
import { useAuth } from '@/contexts/AuthContext';
import { toUserId } from '@/utils/id-helpers';
import type { UserDetailsResponse } from '@low6-pickem/api-types';

export interface User {
  id: string;
  username: string;
  rank?: number;
  points?: number;
  country?: string;
  referralCode?: string;
  ticketsCount?: number;
  streakCount?: number;
}

// Transform API user to UI format
function transformUser(apiUser: UserDetailsResponse): User {
  return {
    id: toUserId(apiUser.user_id),
    username: apiUser.username,
    rank: 0,
    points: 0,
    country: '',
    referralCode: apiUser.referral_code,
    ticketsCount: 0,
    streakCount: 0,
  };
}

export const useUser = () => {
  const { isAuthenticated: isSignedIn } = useAuth();
  const userDetailsQuery = useUserDetails();

  // Get current user from API or stored data
  const currentUser: User | null = useMemo(() => {
    if (userDetailsQuery.data) {
      const user = transformUser(userDetailsQuery.data);
      // Store user data for offline access
      // Note: email and avatar fields may not exist in API response
      const userData = userDetailsQuery.data as {
        email?: string;
        avatar?: string;
      } & typeof userDetailsQuery.data;
      setStoredUser({
        id: user.id,
        username: user.username,
        email: userData.email ?? '',
        avatar: userData.avatar ?? '',
      });
      return user;
    }

    // Fallback to stored user if API is loading
    const storedUser = getStoredUser();
    if (storedUser) {
      type UserWithOptionalStats = {
        id: string;
        username: string;
        rank: number | undefined;
        points: number | undefined;
      };

      return {
        id: storedUser.id,
        username: storedUser.username,
        rank: undefined,
        points: undefined,
      } as UserWithOptionalStats;
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsQuery.data]);

  // Check if a username is the current user
  const isCurrentUser = (username: string): boolean => {
    return username === currentUser?.username;
  };

  // Check if user is authenticated
  const isAuthenticated = (): boolean => {
    return isSignedIn && !!currentUser;
  };

  // Get user stats
  const getUserStats = () => {
    if (!userDetailsQuery.data) return null;

    const streakCount = 0;

    const ticketsCount = 0;

    return {
      totalPickems: 0, // Would come from stats endpoint
      wins: 0,
      losses: 0,
      winPercentage: 0,
      currentStreak: streakCount,
      bestStreak: 0,
      totalPoints: 0,
      averagePoints: 0,
      ticketsAvailable: ticketsCount,
    };
  };

  // Refresh user data
  const refreshUser = async () => {
    await userDetailsQuery.refetch();
  };

  return {
    currentUser,
    isCurrentUser,
    isAuthenticated,
    getUserStats,
    refreshUser,
    // Query states
    isLoading: userDetailsQuery.isLoading,
    error: userDetailsQuery.error,
    isRefreshing: userDetailsQuery.isRefetching,
  };
};
